import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { BuilderFlow } from '@/components/builder/BuilderFlow';
import { BuilderHeader } from '@/components/builder/BuilderHeader';
import { BuilderSettings } from '@/components/builder/BuilderSettings';
import { BuilderStyle } from '@/components/builder/BuilderStyle';
import { BuilderTriggers } from '@/components/builder/BuilderTriggers';
import { BuilderIntegrations } from '@/components/builder/BuilderIntegrations';
import { BuilderDashboard } from '@/components/builder/BuilderDashboard';
import { BuilderKnowledgeBase } from '@/components/builder/BuilderKnowledgeBase';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import PortalLayout from '@/components/portal/PortalLayout';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { useBuilder } from '@/contexts/BuilderContext';
import { useBuilderSave } from '@/hooks/useBuilderSave';
import { TriggerRule } from '@/types/theme';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const TAB_DASHBOARD = "dashboard";
const TAB_FLOW = "flow";
const TAB_SETTINGS = "settings";
const TAB_STYLE = "style";
const TAB_KNOWLEDGE = "knowledge";
// const TAB_TRIGGERS = "triggers";
// const TAB_INTEGRATIONS = "integrations";

export const PortalBuilderContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  
  // Get builder context
  const {
    state,
    updateSettings,
    updateTheme,
    updateQuestions,
    updateTriggerRules,
    updateBasicInfo,
    setDirty,
    resetDirty,
    setActiveTab,
    clearPendingFiles,
    getChatbot,
    getSettings,
    getTheme,
    getQuestions,
    getTriggerRules,
    debouncedSave,
    saveTriggerRules,
    refetch,
    mutations
  } = useBuilder();
  
  // Get all data from context (single source of truth)
  const chatbot = getChatbot();
  
  // Memoize chatMode to prevent unnecessary re-renders
  const chatMode = useMemo(() => state.chatbot?.settings?.chatMode || 'ai_only', [state.chatbot?.settings?.chatMode]);

  // Use centralized save hook
  const { saveChatbotData, saveSettings, saveBasicInfo, saveTheme, isSaving } = useBuilderSave({
    chatbotId: chatbot?.id || '',
    onSuccess: () => {
      resetDirty();
      refetch();
    }
  });

  // Manual save function using centralized service
  const handleManualSave = useCallback(async () => {
    if (!chatbot) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No chatbot data available for save'
      });
      return;
    }

    const settings = getSettings();
    const triggerRules = getTriggerRules();
    const questions = getQuestions();
    const theme = getTheme();

    console.log('💾 Saving all chatbot changes via centralized service:', {
      chatbotId: chatbot.id,
      hasSettings: !!settings,
      hasTriggerRules: triggerRules?.length > 0,
      questionsCount: questions?.length || 0,
      hasTheme: !!theme,
      hasAiSettings: !!settings?.ai_settings,
      aiSettingsKeys: settings?.ai_settings ? Object.keys(settings.ai_settings) : []
    });

    try {
      // 1. Save questions first (if there are any changes)
      if (mutations) {
        const { addQuestion, updateQuestion, deleteQuestion, updateQuestionsOrder } = mutations;
        
        // Get original questions from database to compare
        const { data: originalQuestions } = await supabase
          .from('questions')
          .select('*')
          .eq('chatbot_id', chatbot.id)
          .order('order', { ascending: true });
        
        const originalQuestionIds = new Set(originalQuestions?.map(q => q.id) || []);
        const currentQuestionIds = new Set(questions.map(q => q.id).filter(id => !id?.startsWith('temp_')));
        
        // Find deleted questions (in original but not in current)
        const deletedQuestionIds = Array.from(originalQuestionIds).filter(id => !currentQuestionIds.has(id));
        for (const questionId of deletedQuestionIds) {
          await deleteQuestion(questionId);
        }
        
        // Separate temp questions (new) from existing questions
        const tempQuestions = questions.filter(q => q.id?.startsWith('temp_'));
        const existingQuestions = questions.filter(q => !q.id?.startsWith('temp_'));
        
        // Save new questions
        for (const question of tempQuestions) {
          const { id, chatbot_id, created_at, updated_at, ...questionData } = question;
          await addQuestion(questionData);
        }
        
        // Update existing questions (only if changed)
        for (const question of existingQuestions) {
          const original = originalQuestions?.find(q => q.id === question.id);
          if (original) {
            // Simple comparison - update if any field changed
            const hasChanges = JSON.stringify(original) !== JSON.stringify(question);
            if (hasChanges) {
              await updateQuestion({
                questionId: question.id,
                updates: question
              });
            }
          }
        }
        
        // Update questions order if changed
        if (questions.length > 0) {
          const orderChanged = questions.some((q, index) => {
            const original = originalQuestions?.find(oq => oq.id === q.id);
            return original?.order !== index;
          });
          
          if (orderChanged) {
            await updateQuestionsOrder(questions);
          }
        }
      }

      // 2. Save theme if changed
      const currentTheme = getTheme();
      if (currentTheme) {
        console.log('💾 Saving theme:', { 
          showtimestamp: currentTheme.showtimestamp, 
          showTimestamp: currentTheme.showTimestamp,
          hasShowtimestamp: 'showtimestamp' in currentTheme,
          hasShowTimestamp: 'showTimestamp' in currentTheme
        });
        await saveTheme(currentTheme);
      }

      // 3. Handle operator avatar upload/removal if needed
      let operatorAvatarUrl = chatbot.operator_avatar_url;
      if (state.pendingOperatorAvatarFile) {
        // Upload operator avatar using service
        const { uploadOperatorAvatar } = await import('@/services/chatbotMutationsService');
        operatorAvatarUrl = await uploadOperatorAvatar(chatbot.id, state.pendingOperatorAvatarFile);
      } else if (state.removeOperatorAvatarOnSave) {
        operatorAvatarUrl = null;
      }

      // 4. Handle company logo upload/removal if needed
      let companyLogoUrl = chatbot.company_logo;
      if (state.pendingCompanyLogoFile) {
        // Upload company logo using service
        const { uploadCompanyLogo } = await import('@/services/chatbotMutationsService');
        companyLogoUrl = await uploadCompanyLogo(chatbot.id, state.pendingCompanyLogoFile);
      } else if (state.removeCompanyLogoOnSave) {
        companyLogoUrl = null;
      }

      // 5. Save main chatbot data using centralized service (includes operator_name, operator_avatar_url, company_name, company_logo, custom_javascript, custom_html)
      const result = await saveChatbotData({
        settings: settings,
        name: chatbot.name,
        description: chatbot.description,
        title: chatbot.title,
        welcome_message: chatbot.welcome_message,
        completion_message: chatbot.completion_message,
        operator_name: chatbot.operator_name,
        operator_avatar_url: operatorAvatarUrl,
        company_name: chatbot.company_name,
        company_logo: companyLogoUrl,
        custom_javascript: chatbot.custom_javascript,
        custom_html: chatbot.custom_html
      }, {
        mergeSettings: true,
        preserveAiSettings: false // Allow AI settings to be saved
      });

      if (!result.success) {
        throw result.error || new Error('Failed to save chatbot');
      }

      // Reset dirty state after successful save
      resetDirty();
      
      // Clear pending files after successful save
      clearPendingFiles();
      
      // Refetch to get latest data
      refetch();
    } catch (error) {
      console.error('❌ Error saving chatbot:', error);
      // Error toast is handled by useBuilderSave
    }
  }, [chatbot, getSettings, getTriggerRules, getQuestions, getTheme, saveChatbotData, saveTheme, saveTriggerRules, mutations, resetDirty, clearPendingFiles, refetch, toast, state.pendingOperatorAvatarFile, state.removeOperatorAvatarOnSave, state.pendingCompanyLogoFile, state.removeCompanyLogoOnSave]);

  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [urlTabInitialized, setUrlTabInitialized] = useState(false);

  // Initialize active tab from URL parameter on mount
  useEffect(() => {
    if (!urlTabInitialized) {
      const tabFromUrl = searchParams.get('tab');
      // const validTabs = [TAB_DASHBOARD, TAB_FLOW, TAB_STYLE, TAB_TRIGGERS, TAB_KNOWLEDGE, TAB_INTEGRATIONS, TAB_SETTINGS];
      const validTabs = [TAB_DASHBOARD, TAB_FLOW, TAB_STYLE, TAB_KNOWLEDGE, TAB_SETTINGS];

      console.log('Initial URL tab setup:', { tabFromUrl, currentActiveTab: state.activeTab });

      if (tabFromUrl && validTabs.includes(tabFromUrl)) {
        console.log('Setting initial tab from URL:', tabFromUrl);
        setActiveTab(tabFromUrl);
      } else if (!tabFromUrl) {
        console.log('No tab in URL, setting default dashboard and updating URL');
        setActiveTab(TAB_DASHBOARD);
        setSearchParams(prev => {
          prev.set('tab', TAB_DASHBOARD);
          return prev;
        });
      }
      setUrlTabInitialized(true);
    }
  }, [searchParams, setActiveTab, setSearchParams, state.activeTab, urlTabInitialized]);

  // Keep URL in sync with tab changes
  useEffect(() => {
    if (urlTabInitialized) {
      const tabFromUrl = searchParams.get('tab');
      if (tabFromUrl !== state.activeTab) {
        console.log('Syncing URL with active tab:', state.activeTab);
        setSearchParams(prev => {
          prev.set('tab', state.activeTab);
          return prev;
        });
      }
    }
  }, [state.activeTab, searchParams, setSearchParams, urlTabInitialized]);

  // Handle tab changes with unsaved changes warning
  const handleTabChange = (newTab: string) => {
    if (state.unsavedChanges) {
      setPendingTab(newTab);
      setIsAlertOpen(true);
    } else {
      setActiveTab(newTab);
      // Update URL parameter
      setSearchParams(prev => {
        prev.set('tab', newTab);
        return prev;
      });
    }
  };

  const discardChangesAndSwitchTab = () => {
    if (pendingTab) {
      setActiveTab(pendingTab);
      resetDirty();
      // Update URL parameter
      setSearchParams(prev => {
        prev.set('tab', pendingTab);
        return prev;
      });
      setPendingTab(null);
    }
    setIsAlertOpen(false);
  };

  const cancelTabSwitch = () => {
    setPendingTab(null);
    setIsAlertOpen(false);
  };

  // DISABLED AUTO-SAVE to prevent race conditions with manual saves
  // Auto-save when settings change (with additional debounce)
  // useEffect(() => {
  //   if (state.isDirty && state.chatbot) {
  //     const chatbot = state.chatbot;
  //     const settings = state.chatbot.settings;
  //
  //     // Add a small delay to prevent rapid-fire saves
  //     const timeoutId = setTimeout(() => {
  //       debouncedSave({
  //         settings: settings,
  //         name: chatbot.name,
  //         description: chatbot.description,
  //         title: chatbot.title,
  //         welcome_message: chatbot.welcome_message,
  //         completion_message: chatbot.completion_message
  //       });
  //     }, 500); // 500ms delay before triggering save
  //
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [state.isDirty, state.chatbot?.id, debouncedSave]); // Only depend on stable values

  // Show loading state
  if (state.isLoading) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-64">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-muted-foreground">Loading chatbot...</p>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show error state
  if (state.error) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center space-y-4">
              <p className="text-destructive">{state.error}</p>
              <button
                onClick={() => refetch()}
                className="text-primary hover:underline mr-4"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/portal/chatbots')}
                className="text-primary hover:underline"
              >
                Back to Chatbots
              </button>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show loading state while chatbot is being loaded
  if (state.isLoading || !chatbot) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading chatbot...</p>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show error state if there's an error loading the chatbot
  if (state.error) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-destructive mb-4">Error loading chatbot: {state.error}</p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Main builder interface
  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <BuilderHeader
              chatbot={chatbot}
              onCopyEmbedCode={() => {
                const embedCode = `<script src="https://app.offerchat.ai/embed.js" data-chatbot-id="${chatbot.id}"></script>`;
                navigator.clipboard.writeText(embedCode);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                toast({
                  title: "Embed code copied!",
                  description: "The embed code has been copied to your clipboard.",
                });
              }}
              copied={copied}
            />
          </div>

          {/* Save Button - shown when there are unsaved changes (excluding trigger rules) */}
          {(() => {
            // Don't show Save Changes button if only trigger rules have changes
            // Trigger rules are saved via their internal Save Changes button
            if (!state.isDirty) return false;
            // If only trigger rules tab has changes, don't show the button
            // if (state.dirtyFields.size === 1 && state.dirtyFields.has(TAB_TRIGGERS)) {
            //   return false;
            // }
            // Show button if there are changes in other tabs (with or without trigger rules)
            return true;
          })() && (
            <Button
              onClick={handleManualSave}
              disabled={isSaving}
              className="ml-4"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
        </div>

        <Separator />
        
        <Tabs value={state.activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value={TAB_DASHBOARD}>Dashboard</TabsTrigger>
            <TabsTrigger value={TAB_FLOW}>Flow</TabsTrigger>
            <TabsTrigger value={TAB_STYLE}>Style</TabsTrigger>
            <TabsTrigger value={TAB_SETTINGS}>Settings</TabsTrigger>
            <TabsTrigger value={TAB_KNOWLEDGE}>Knowledge</TabsTrigger>
            {/*<TabsTrigger value={TAB_TRIGGERS}>Triggers</TabsTrigger>*/}
            {/*<TabsTrigger value={TAB_INTEGRATIONS}>Integrations</TabsTrigger>*/}
          </TabsList>
          
          <TabsContent value={TAB_DASHBOARD} className="space-y-4">
            <BuilderDashboard 
              chatbot={chatbot}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_DASHBOARD, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_FLOW} className="space-y-4">
            <BuilderFlow
              chatFlow={{
                welcomeMessage: chatbot.welcome_message,
                completionMessage: chatbot.completion_message,
                questions: getQuestions()
              }}
              onUpdateChatFlow={(updates) => {
                updateBasicInfo({
                  welcome_message: updates.welcomeMessage,
                  completion_message: updates.completionMessage
                });
              }}
              questionTypes={[
                { value: "text", label: "Text" },
                { value: "number", label: "Number" },
                { value: "button", label: "Button" },
                { value: "boolean", label: "Yes/No" },
                { value: "no_response", label: "No Response" },
              ]}
              onAddQuestion={async () => {
                // Create temporary question ID for local state
                const tempId = `temp_${Date.now()}`;
                const newQuestion = {
                  id: tempId,
                  chatbot_id: chatbot.id,
                  text: '',
                  type: 'text' as const,
                  options: [],
                  order: getQuestions().length,
                  shortname: '',
                  errormessage: '',
                  responsevalidation: '',
                  validation_rules: {},
                  validationType: 'none',
                  placeholder: '',
                  rich_content: null,
                  enable_ai_response: false,
                  ai_response_prompt: null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                };
                
                // Add to local state only
                updateQuestions([...getQuestions(), newQuestion]);
                // Mark as dirty - user needs to click Save Changes
                setDirty(TAB_FLOW, true);
              }}
              onUpdateQuestion={async (questionId, updates) => {
                // Skip database update for temporary questions
                if (questionId.startsWith('temp_')) {
                  const questions = getQuestions().map(q => 
                    q.id === questionId ? { ...q, ...updates } : q
                  );
                  updateQuestions(questions);
                  return;
                }
                
                if (mutations?.updateQuestion) {
                  try {
                    // Update in database
                    await mutations.updateQuestion({ questionId, updates });
                    
                    // Update local state
                    const questions = getQuestions().map(q => 
                      q.id === questionId ? { ...q, ...updates } : q
                    );
                    updateQuestions(questions);
                  } catch (error) {
                    console.error('Failed to update question:', error);
                    toast({
                      title: 'Error updating question',
                      description: 'Failed to update the question. Please try again.',
                      variant: 'destructive'
                    });
                  }
                }
              }}
              onDeleteQuestion={async (questionId) => {
                // Update local state only, don't delete from database yet
                const questions = getQuestions().filter(q => q.id !== questionId);
                updateQuestions(questions);
                // Mark as dirty - user needs to click Save Changes
                setDirty(TAB_FLOW, true);
              }}
              onReorderQuestions={async (reorderedQuestions) => {
                // Update local state only, don't save to database yet
                updateQuestions(reorderedQuestions);
                // Mark as dirty - user needs to click Save Changes
                setDirty(TAB_FLOW, true);
              }}
              onAddOption={(questionId) => {
                const questions = getQuestions().map(q => 
                  q.id === questionId 
                    ? { ...q, options: [...(q.options || []), { label: '', value: '' }] }
                    : q
                );
                updateQuestions(questions);
              }}
              onUpdateOption={(questionId, optionIndex, value) => {
                const questions = getQuestions().map(q => {
                  if (q.id === questionId && q.options) {
                    const updatedOptions = [...q.options];
                    updatedOptions[optionIndex] = { label: value, value };
                    return { ...q, options: updatedOptions };
                  }
                  return q;
                });
                updateQuestions(questions);
              }}
              onDeleteOption={(questionId, optionIndex) => {
                const questions = getQuestions().map(q => {
                  if (q.id === questionId && q.options) {
                    const updatedOptions = q.options.filter((_, index) => index !== optionIndex);
                    return { ...q, options: updatedOptions };
                  }
                  return q;
                });
                updateQuestions(questions);
              }}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_FLOW, isDirty)}
              chatbotId={chatbot.id}
              chatMode={chatMode}
              onChatModeChange={async (newChatMode) => {
                console.log('🔄 Chat mode change in PortalBuilderContent:', newChatMode);
                
                // Update local settings only, don't save yet
                updateSettings({ chatMode: newChatMode });
                
                // Mark as dirty - user needs to click Save Changes
                setDirty(TAB_FLOW, true);
              }}
            />
          </TabsContent>
          
          <TabsContent value={TAB_STYLE} className="space-y-4">
            <BuilderStyle
              theme={getTheme()}
              onUpdateTheme={(updatedTheme) => {
                // Update local theme only, don't save yet
                updateTheme(updatedTheme);
                // Mark as dirty - user needs to click Save Changes
                setDirty(TAB_STYLE, true);
              }}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_STYLE, isDirty)}
            />
          </TabsContent>

          <TabsContent value={TAB_SETTINGS} className="space-y-4">
            <BuilderSettings
                chatbot={chatbot}
                onUpdateSettings={(updatedSettings) => {
                  // Check if this is a custom code update (custom_javascript or custom_html)
                  if ('custom_javascript' in updatedSettings || 'custom_html' in updatedSettings) {
                    // Update basic info for custom code fields
                    updateBasicInfo(updatedSettings as any);
                  } else {
                    // Update settings for other fields
                    updateSettings(updatedSettings);
                  }
                  // Mark as dirty - user needs to click Save Changes
                  setDirty(TAB_SETTINGS, true);
                }}
                reportUnsavedChanges={(isDirty) => setDirty(TAB_SETTINGS, isDirty)}
            />
          </TabsContent>

          <TabsContent value={TAB_KNOWLEDGE} className="space-y-4">
            <BuilderKnowledgeBase
                chatbotId={chatbot.id}
                reportUnsavedChanges={(isDirty) => setDirty(TAB_KNOWLEDGE, isDirty)}
            />
          </TabsContent>
          
          {/*<TabsContent value={TAB_TRIGGERS} className="space-y-4">*/}
          {/*  <BuilderTriggers*/}
          {/*    chatbotId={chatbot.id}*/}
          {/*    reportUnsavedChanges={(isDirty) => setDirty(TAB_TRIGGERS, isDirty)}*/}
          {/*  />*/}
          {/*</TabsContent>*/}
          
          {/*<TabsContent value={TAB_INTEGRATIONS} className="space-y-4">*/}
          {/*  <BuilderIntegrations*/}
          {/*    chatbotId={chatbot.id}*/}
          {/*    reportUnsavedChanges={(isDirty) => setDirty(TAB_INTEGRATIONS, isDirty)}*/}
          {/*  />*/}
          {/*</TabsContent>*/}
        </Tabs>
        
        <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to leave this tab?
                Your changes will be lost if you don't save them.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelTabSwitch}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={discardChangesAndSwitchTab}>Discard Changes</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PortalLayout>
  );
};
